import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ChevronLeft, ChevronRight, Plus, Loader2 } from 'lucide-react';
import SEO from '@/components/SEO';

const PORTRAIT_COUNT = 160;
const LANDSCAPE_COUNT = 96;
const BATCH_SIZE = 24;

// Build an interleaved list (2 portraits : 1 landscape) for a balanced masonry
function buildImages() {
  const portraits = Array.from({ length: PORTRAIT_COUNT }, (_, i) => ({
    id: `p${i + 1}`,
    src: `/images/gallery/portrait_${i + 1}.jpg`,
    height: 'tall',
  }));
  const landscapes = Array.from({ length: LANDSCAPE_COUNT }, (_, i) => ({
    id: `l${i + 1}`,
    src: `/images/gallery/landscape_${i + 1}.jpg`,
    height: 'short',
  }));
  const out = [];
  let pi = 0;
  let li = 0;
  while (pi < portraits.length || li < landscapes.length) {
    if (pi < portraits.length) out.push(portraits[pi++]);
    if (pi < portraits.length) out.push(portraits[pi++]);
    if (li < landscapes.length) out.push(landscapes[li++]);
  }
  return out;
}

export default function Gallery() {
  const allImages = useMemo(buildImages, []);
  const [visible, setVisible] = useState(BATCH_SIZE);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);

  const images = allImages.slice(0, visible);
  const hasMore = visible < allImages.length;

  const loadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setVisible((v) => Math.min(v + BATCH_SIZE, allImages.length));
      setLoadingMore(false);
    }, 250);
  };

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setImageIndex(index);
  };
  const closeLightbox = () => setSelectedImage(null);
  const nextImage = () => {
    const newIndex = (imageIndex + 1) % images.length;
    setImageIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };
  const prevImage = () => {
    const newIndex = (imageIndex - 1 + images.length) % images.length;
    setImageIndex(newIndex);
    setSelectedImage(images[newIndex]);
  };

  const getGridSpan = (height) => (height === 'tall' ? 'row-span-2' : 'row-span-1');

  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid="gallery-page">
      <SEO
        title="Gallery"
        description="Browse photos from Frequency Forest at Maitland Showground — festival vibes, stages, art and unforgettable moments."
      />
      {/* Header */}
      <section className="py-16 md:py-24 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="badge mb-4">Visual Journey</span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ff-brown mb-4">
              Gallery
            </h1>
            <p className="text-ff-brown/60 max-w-xl mx-auto">
              Moments from the forest — tap any photo to view.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Masonry Grid */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 auto-rows-[180px]">
            {images.map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, delay: Math.min((index % BATCH_SIZE) * 0.02, 0.4) }}
                className={`relative overflow-hidden group cursor-pointer rounded-2xl border-2 border-ff-outline/20 bg-white ${getGridSpan(image.height)}`}
                onClick={() => openLightbox(image, index)}
                data-testid={`gallery-image-${image.id}`}
              >
                <img
                  src={image.src}
                  alt="Frequency Forest festival"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-ff-brown/0 group-hover:bg-ff-brown/40 transition-all duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn className="w-8 h-8 text-white" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Load More */}
      <section className="pb-24 text-center">
        {hasMore ? (
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 bg-ff-yellow text-ff-brown px-8 py-4 rounded-full font-bold hover:brightness-105 transition-all disabled:opacity-70"
            data-testid="gallery-load-more-btn"
          >
            {loadingMore ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
            Load more photos
          </button>
        ) : (
          <p className="text-ff-brown/50 text-sm" data-testid="gallery-all-loaded">
            That's all {allImages.length} photos — see you in the forest!
          </p>
        )}
        <p className="text-ff-brown/40 text-xs mt-4" data-testid="gallery-count">
          Showing {images.length} of {allImages.length}
        </p>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="gallery-lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white hover:text-ff-yellow transition-colors z-10"
              onClick={closeLightbox}
              data-testid="lightbox-close-btn"
            >
              <X className="w-8 h-8" />
            </button>

            <button
              className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 text-white hover:text-ff-yellow transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              data-testid="lightbox-prev-btn"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
            <button
              className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 text-white hover:text-ff-yellow transition-colors z-10"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              data-testid="lightbox-next-btn"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-5xl max-h-[85vh] px-14 sm:px-16"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt="Frequency Forest festival"
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
